import endpoints from '@app/src/api/endpoints'
import useBaselineAssessmentToPDF from '@app/src/components/ExportToPDF/useBaselineAssessmentToPDF'
import LoadingButton from '@app/src/components/LoadingButton'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import PreFetchedSingleFilterFacet from '@app/src/pages/ResourceCollection/Filters/PreFetchedSingleFilterFacet'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import type { Provider } from '@app/src/types/organizations'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { Download } from '@mui/icons-material'
import { Box } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { FiltersProps } from '..'
import FilterSelect from './FilterSelect'

type AssessmentCompanyPageFiltersProps = {
  provider: Provider
  contentRef: React.RefObject<HTMLDivElement>
  enabledAssessmentTemplates: AssessmentTemplate[]
  assessmentTemplateId: number
  setMaxRows: (value: number) => void
  showPdfExportButton?: boolean
} & FiltersProps

const AssessmentCompanyPageFilters: React.FC<AssessmentCompanyPageFiltersProps> = ({
  provider,
  contentRef,
  allowedFilters,
  enabledAssessmentTemplates,
  assessmentTemplateId,
  setMaxRows,
  resetPage,
  showPdfExportButton,
}) => {
  const { formatMessage } = useIntl()

  const assessmentTemplateName = enabledAssessmentTemplates?.find(
    template => template.id === assessmentTemplateId,
  )?.name

  const assessmentTemplateType = enabledAssessmentTemplates?.find(
    template => template.id === assessmentTemplateId,
  )?.assessmentType

  const reorderedTemplates = React.useMemo(() => {
    const baseline = enabledAssessmentTemplates.find(
      template => template.assessmentType === AssessmentType.BaselineAssessment,
    )
    const automated = enabledAssessmentTemplates.filter(
      template => template.assessmentType === AssessmentType.StandardAssessment,
    )
    const flexible = enabledAssessmentTemplates.filter(template => template.assessmentType === AssessmentType.Flexible)
    return baseline ? [baseline, ...automated, ...flexible] : enabledAssessmentTemplates
  }, [enabledAssessmentTemplates])

  const { generatePDF, isLoading } = useBaselineAssessmentToPDF(
    contentRef,
    provider,
    assessmentTemplateName,
    assessmentTemplateType,
    setMaxRows,
  )

  const handleExportToPdf = async () => {
    await generatePDF()
  }

  return (
    <Box>
      <Filters allowedFilters={allowedFilters} resetPage={resetPage}>
        {() => (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" gap={3}>
              <Box width={400}>
                <Filter name="assessmentTemplate.id" operator={Operators.EqualTo}>
                  {({ value, onChange }) => (
                    <FilterSelect
                      size="small"
                      options={
                        reorderedTemplates.map(a => ({
                          label: a.name,
                          value: a.id.toString(),
                        })) ?? []
                      }
                      multiple={false}
                      value={value as string}
                      onChange={(value, option, replace) => onChange(value, replace)}
                      fieldLabel={formatMessage({ id: 'schemas.filter.assessment' })}
                      preSelectedValue={reorderedTemplates?.[0]?.name}
                    />
                  )}
                </Filter>
              </Box>
              <Box width={160}>
                <Filter name="periodName" operator={Operators.EqualTo}>
                  {({ value, onChange, filterName }) => (
                    <PreFetchedSingleFilterFacet
                      implicitFilters={[
                        {
                          name: 'providerId',
                          filters: [{ operator: Operators.EqualTo, value: provider.id }],
                        },
                        {
                          name: 'assessmentTemplateId',
                          filters: [{ operator: Operators.EqualTo, value: assessmentTemplateId }],
                        },
                      ]}
                      endpoint={endpoints.assessmentFacet}
                      filterName={filterName}
                      isEnum
                      onChange={value => onChange(value, true)}
                      fieldLabel={formatMessage({ id: 'schemas.request.period' })}
                      value={[value].flat()}
                      sort={{ target: 'label', order: SortOrder.DESCENDING }}
                    />
                  )}
                </Filter>
              </Box>
            </Box>

            {showPdfExportButton && (
              <LoadingButton
                startIcon={<Download />}
                variant="outlined"
                size="small"
                onClick={handleExportToPdf}
                loading={isLoading}
                sx={{
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  ml: 3,
                }}
              >
                Export to PDF
              </LoadingButton>
            )}
          </Box>
        )}
      </Filters>
    </Box>
  )
}

export default AssessmentCompanyPageFilters
